import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2, Divider } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ControlledRichTextEditor from "@components/inputs/controlledRichTextEditor";
import ImagePicker from "@components/inputs/imagePicker";
import ImageMultiPicker from "@components/inputs/imageMultiPicker";

import CreateConfirmDialog from "./createConfirmDialog";

import { useSnackbar } from "@context/snackbarContext";
import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";
import { formatDateTime } from "@utils/formatter";


const schema = z.object({
  blogName: z.string().min(1, 'Blog Name is required'),

  maxApplications: z.union([
    z.string()
      .min(1, "Max Applicants is required")
      .regex(/^\d+$/, "Max Applicants must be a whole number"),
    z.number()
      .int("Max Applicants must be a whole number")
      .positive("Max Applicants must be a positive number"),
  ]),

  headerCntrad: z.string().min(1, { message: '標頭 is required' }),
  contentCntrad: z.string().min(1, { message: '內容 is required' }),

  headerCnsimp: z.string().min(1, { message: '标头 is required' }),
  contentCnsimp: z.string().min(1, { message: '內容 is required' }),

  headerEng: z.string().min(1, { message: 'Header is required' }),
  contentEng: z.string().min(1, { message: 'Content is required' }),

  /* view only */
  createdOn: z.string().optional(),
  createdBy: z.string().optional(),
})

const ACTION = {
  UPDATE: 'Update',
  PUBLISH: 'Publish',
  UNLIST: 'Unlist'
}

export default function BlogForm() {

  const queryClient = useQueryClient()
  const openSnackbar = useSnackbar();
  const navigate = useNavigate()
  const { pathname } = useLocation();
  const { user, isCurrentModuleAdmin } = useAuthentication();
  const pageAction = pathname.split("/")[3] || null; // get from pathname expected values ["create" || "view" || "edit"]
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");


  const { handleSubmit, control, getValues, reset, watch } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
    }
  });

  const [thumbnail, setThumbnail] = useState(false)
  const [slideshow, setSlideshow] = useState([])

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [openCreateConfirmDialog, setOpenCreateConfirmDialog] = useState(false)
  const [confirmDialogAction, setConfirmDialogAction] = useState(ACTION.CREATE)
  const [status, setStatus] = useState(false)

  useEffect(()=>{
    if (((pageAction === "edit" || pageAction === "view") && !id) || (pageAction === "create" && id)) {
      onBack()
    }
    if (id) {
      fetchBlogMutation.mutate(id)
    }
  },[pageAction])

  const fetchBlogMutation = useMutation({
    mutationFn: (id) => {
      return axios.get(`${AdminApiEndpoints.getFeaturedDonation}/${id}`)
    },
    onSuccess: (res) => {
      const {createdOn, ...data} = res.data
      // console.log(data)
      const createdOnFormatted = formatDateTime(createdOn)
      reset({...data, createdOn: createdOnFormatted})
      setStatus(data?.status)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve blog", "error");
    }
  });
  

  const createBlogMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(AdminApiEndpoints.createFeaturedDonation, {...data, createdBy: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      setOpenCreateConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getBlog'] })
      openSnackbar("Successfully created blog", "success");
      onBack()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      setOpenCreateConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create blog", "error");
    }
  })

  const updateBlogMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateFeaturedDonation}/${id}`, {...data, updatedBy: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getBlog'] })
      openSnackbar("Successfully updated blog", "success");
      fetchBlogMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update blog", "error");
    }
  })

  const updateBlogStatusMutation = useMutation({
    mutationFn: (data) => {
      return axios.put(`${AdminApiEndpoints.updateFearuredDonationStatus}/${id}`, {status: data?.status, updatedBy: user?.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      queryClient.invalidateQueries({ queryKey: ['getBlog'] })
      openSnackbar("Successfully updated blog status", "success");
      fetchBlogMutation.mutate(id)
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar("Failed to update blog status", "error");
    }
  })

  const onSubmit = () => {
    if(!thumbnail){
      openSnackbar(`Please upload thumbnail`, "error");
      return;
    }
    if(slideshow.length === 0){
      openSnackbar(`Please upload slideshow`, "error");
      return;
    }
    if(pageAction === "create"){
      setOpenCreateConfirmDialog(true)
      return
    }
    setConfirmDialogAction(ACTION.UPDATE)
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = ({checked = true}) => {
    const {...data} = getValues();
    
    if(pageAction === "create")
      createBlogMutation.mutate({...data, status: checked})
    else{
      if(confirmDialogAction === ACTION.PUBLISH){
        updateBlogStatusMutation.mutate({status: true})
      }
      else if(confirmDialogAction === ACTION.UNLIST){
        updateBlogStatusMutation.mutate({status: false})
      }
      else if(confirmDialogAction === ACTION.UPDATE){
        updateBlogMutation.mutate({...data})
      }
    }
      
  }

  const onBack = () => {
    navigate('/admin/blog', { replace: true });
  }

  // console.log(watch('content'))

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs 
        rightSection={
          (!fetchBlogMutation.isPending && !fetchBlogMutation.isError) && isCurrentModuleAdmin && pageAction === "edit" && 
          <div style={{display: 'flex', gap: "10px"}}>
            <CustomButton color="#4DB6AC" disabled={!!(status)} onClick={()=>{setConfirmDialogAction(ACTION.PUBLISH);setOpenConfirmDialog(true)}}>
              Publish
            </CustomButton>
            <CustomButton color="#D13F58" disabled={!(status)} onClick={()=>{setConfirmDialogAction(ACTION.UNLIST);setOpenConfirmDialog(true)}}>
              Unlist
            </CustomButton>
          </div>
        }
      />
      <SkeletonWrapper isLoading={fetchBlogMutation.isPending} isError={fetchBlogMutation.isError} errorMessage="Unable to load featured" disabled={["create"].includes(pageAction)}>
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Blog Details</Typography>
            </Grid2>

            <Grid2 size={12}>
              <ImagePicker 
                name="thumbnail"
                label={"Upload Thumbnail (600x400)"}
                value={thumbnail}
                onChange={setThumbnail}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>

            <Grid2 size={12}>
              <ControlledTextField 
                name="blogName"
                control={control}
                label="Blog Name"
                disabled={fetchBlogMutation.isPending || fetchBlogMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>

            {["view", "edit"].includes(pageAction) && <>

            <Grid2 size={{ xs: 12 }}>
              <Divider />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdOn"
                control={control}
                label="Created On"
                disabled={true}
              />
            </Grid2>

            <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
              <ControlledTextField 
                name="createdBy"
                control={control}
                label="Created By"
                disabled={true}
              />
            </Grid2>

            </>}

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>Slideshow</Typography>
            </Grid2>

            <Grid2 size={12}>
              <ImageMultiPicker 
                name="slideshow"
                label={"Upload 5 Photo(s) (1000x500)"}
                value={slideshow}
                onChange={setSlideshow}
                disabled={["view"].includes(pageAction)}
                maxImages={5}
              />
            </Grid2>

          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>繁体中文</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="headerCntrad"
                control={control}
                label="標頭"
                disabled={fetchBlogMutation.isPending || fetchBlogMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentCntrad"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>简体中文</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="headerCnsimp"
                control={control}
                label="标头"
                disabled={fetchBlogMutation.isPending || fetchBlogMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentCnsimp"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
          }}
        >
          <Grid2 container spacing={"20px"}>
            <Grid2 size={12}>
              <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>English</Typography>
            </Grid2>
            <Grid2 size={12}>
              <ControlledTextField 
                name="headerEng"
                control={control}
                label="Header"
                disabled={fetchBlogMutation.isPending || fetchBlogMutation.isError}
                readOnly={["view"].includes(pageAction)}
              />
            </Grid2>
            <Grid2 size={12}>
              <ControlledRichTextEditor 
                name="contentEng"
                control={control}
                disabled={["view"].includes(pageAction)}
              />
            </Grid2>
          </Grid2>
        </Paper>

        {["create", "edit"].includes(pageAction) && 
          <Paper
            sx={{
              p: "20px 25px",
              boxShadow: "none",
              borderRadius: "10px",
              border: "1px solid #E5E5E5",
              overflowX: "auto",
              display: "flex",
              justifyContent: 'flex-end',
              gap: "10px"
            }}
          >
            <CustomButton type="submit" disabled={createBlogMutation.isPending || fetchBlogMutation.isError}>Submit</CustomButton>
          </Paper>
        }
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription={`${confirmDialogAction} blog?`}
        onSubmit={()=>onConfirmSubmit({checked: false})}
        loading={createBlogMutation.isPending || updateBlogMutation.isPending}
      />
      <CreateConfirmDialog 
        open={openCreateConfirmDialog}
        onClose={()=>setOpenCreateConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Are you sure you want to create this blog?"
        onSubmit={onConfirmSubmit}
        loading={createBlogMutation.isPending || updateBlogMutation.isPending}
      />
    </div>
  );
}
